import { useState, useMemo, useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from "react";
import { requireAuthForBuy } from "@/lib/softAuthGate";
import { loadConditionGuide, type ConditionGuideEntry } from "@/components/ConditionGuide";
import {
  catalog, getCategoryLabels, getCategoryOrder, grades, screenConditions,
  formatPrice, getStorageLabel, getVariantConfig, calculateFinalPrice, getPriceKey, convertFromGHS,
  getDeviceVariants, getDeviceDisplayName,
  type DeviceCategory, type DeviceModel, type Grade, type ScreenCondition, type DisplayCurrency,
} from "@/data/catalog";
import { useAdminSettings, getOrderedGrades, getGradeColor } from "@/stores/adminStore";
import { addOrder } from "@/data/orderStore";
import { addEnquiry } from "@/data/enquiryStore";
import { hasStockTracking, getStockStatus } from "@/stores/stockStore";
import { getDeviceSettings, getActiveDevices, isVariantEnquiryOnly, isVariantEnabled, useDeviceSettingsRevision } from "@/stores/deviceSettingsStore";
import { isSubconditionEnabled, getModelLabel, getGradeLabel, areGradesEnabled, getCategorySettings, getEffectiveScreenConditions } from "@/data/categorySettingsStore";
import { getEnabledSubVariantsForDevice, type CustomSubVariant } from "@/data/customSubVariantStore";
import { getEnabledShippingOptions, type ShippingOption } from "@/stores/shippingStore";
import { useShippingStore } from "@/stores/shippingStore";
import { useConditionGuideStore } from "@/stores/conditionGuideStore";
import { MessageCircle, ChevronDown, AlertTriangle, PackageX, Phone, Tag, Palette, Settings2, MapPin, Loader2, HelpCircle } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { sanitizeText, sanitizePhone, isValidPhoneNumber } from "@/lib/sanitize";
import { useStorefrontSettings } from "@/stores/storefrontSettingsStore";
import { useSwapStore } from "@/stores/swapStore";
import DeviceMediaGallery from "@/components/DeviceMediaGallery";
import { useAuth } from "@/hooks/useAuth";
import ConditionGuide from "@/components/ConditionGuide";
import CustomerReviews from "@/components/CustomerReviews";
import AdSlotRenderer from "@/components/AdSlotRenderer";
import { useExchangeRate } from "@/hooks/useExchangeRate";
import { useToast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { backfillCustomerProfile } from "@/lib/customerBackfill";
import { useTenantLockdown } from "@/hooks/useTenantLockdown";
import { useRoutedWhatsAppOpener } from "@/lib/whatsapp";


// Returning customer storage
const RETURNING_CUSTOMER_KEY = "khays_returning_customer";
interface ReturningCustomer { name: string; location: string; phone: string; savedAt: number; }

function loadReturningCustomer(): ReturningCustomer | null {
  try {
    const s = localStorage.getItem(RETURNING_CUSTOMER_KEY);
    if (s) {
      const c = JSON.parse(s) as ReturningCustomer;
      // Expire after 90 days
      if (Date.now() - c.savedAt < 90 * 86400 * 1000) return c;
    }
  } catch {}
  return null;
}

function saveReturningCustomer(name: string, location: string, phone: string): void {
  localStorage.setItem(RETURNING_CUSTOMER_KEY, JSON.stringify({ name, location, phone, savedAt: Date.now() }));
}

// Check if phone matches a previous order
function findPreviousCustomerByPhone(phone: string): ReturningCustomer | null {
  try {
    const ordersRaw = localStorage.getItem("khays_orders");
    if (!ordersRaw) return null;
    const orders = JSON.parse(ordersRaw);
    const normalizedPhone = phone.replace(/\D/g, "").slice(-10);
    for (const o of orders) {
      if (o.customerPhone) {
        const orderPhone = o.customerPhone.replace(/\D/g, "").slice(-10);
        if (orderPhone === normalizedPhone && orderPhone.length >= 10) {
          return { name: o.customerName, location: o.location, phone: o.customerPhone, savedAt: Date.now() };
        }
      }
    }
  } catch {}
  return null;
}

export interface OrderFormHandle {
  selectDevice: (category: string, model: string) => void;
}

interface OrderFormProps {
  referralStaff?: string;
  staffCommissionPercent?: number;
  compact?: boolean;
  /** Notify parent when the active media key (device|variant|grade|screen) changes — used by Minimal storefront to drive the gallery. */
  onMediaKeyChange?: (key: string) => void;
  /** Override the shipping section label (e.g. "Delivery Options" for Modern's Available Now overlay). */
  shippingLabel?: string;
  /** When provided, replaces the global shipping options for this overlay. */
  shippingOptionsOverride?: import("@/stores/shippingStore").ShippingOption[];
  /**
   * When provided, layers per-tab pricing/margin overrides on top of the global
   * adminSettings (Modern Available Now / Special Offers tabs use this so the
   * overlay reflects the same prices as the menu listings).
   */
  adminSettingsOverride?: any;
  /**
   * When provided, forces the exchange-rate used for Yuan-priced devices in this
   * overlay only (e.g. tab opted out of `applyExchangeRate`).
   */
  forceRate?: number;
  /**
   * Per-tab hidden price keys (Modern Available Now / Special Offers). Merged on
   * top of global `sfSettings.hiddenPriceCombos` so menu-scoped hides also remove
   * variants/grades/screens/sub-variants from the overlay chips.
   */
  hiddenCombosOverride?: Record<string, boolean>;
}

const OrderForm = forwardRef<OrderFormHandle, OrderFormProps>(({ referralStaff, staffCommissionPercent = 0, compact = false, onMediaKeyChange, shippingLabel, shippingOptionsOverride, adminSettingsOverride, forceRate, hiddenCombosOverride }, ref) => {
  const { toast } = useToast();
  const { user: authUser } = useAuth();
  const { rate: liveRate, usdRate, loading: rateLoading, error: rateError } = useExchangeRate();
  const rate = typeof forceRate === "number" ? forceRate : liveRate;
  const [selectedCategory, setSelectedCategory] = useState<DeviceCategory | "">("");
  const [selectedModel, setSelectedModel] = useState("");
  const [selectedVariant, setSelectedVariant] = useState("");
  const [selectedGrade, setSelectedGrade] = useState<string>("Grade A");
  const [selectedScreen, setSelectedScreen] = useState<ScreenCondition>("Original Screen");
  const [selectedColor, setSelectedColor] = useState<string>("");
  const [selectedShipping, setSelectedShipping] = useState<string>("");
  const [selectedSubVariants, setSelectedSubVariants] = useState<Record<string, string>>({});
  const [customerName, setCustomerName] = useState("");
  const [customerLocation, setCustomerLocation] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [geoLoading, setGeoLoading] = useState(false);

  // Inquiry popup
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [inquiryName, setInquiryName] = useState("");
  const [inquiryLocation, setInquiryLocation] = useState("");
  const [inquiryPhone, setInquiryPhone] = useState("");
  const [contactInquiryMode, setContactInquiryMode] = useState(false);

  // Returning customer detection
  const [isReturning, setIsReturning] = useState(false);
  const shippingRev = useShippingStore(s => s._rev);
  const conditionGuideRev = useConditionGuideStore(s => s._rev);

  // Load returning customer data on mount
  useEffect(() => {
    const saved = loadReturningCustomer();
    if (saved) {
      setCustomerName(saved.name);
      setCustomerLocation(saved.location);
      setCustomerPhone(saved.phone);
      setIsReturning(true);
    }
  }, []);

  const formRef = useRef<HTMLDivElement>(null);
  const priceRef = useRef<HTMLDivElement>(null);
  const deviceSettingsRev = useDeviceSettingsRevision();

  useImperativeHandle(ref, () => ({
    selectDevice: (category: string, model: string) => {
      setSelectedCategory(category as DeviceCategory);
      setSelectedModel(model);
      setSelectedVariant("");
      setSelectedGrade("Grade A");
      setSelectedScreen("Original Screen");
      setSelectedColor("");
      setSelectedSubVariants({});
      setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    },
  }), []);

  const allDevices = useMemo(() => getActiveDevices(), [deviceSettingsRev]);
  const baseAdminSettings = useAdminSettings();
  // When an overlay supplies per-tab overrides (e.g. Modern's Available Now / Special Offers),
  // merge them on top of the global admin settings so price/margin/extra-profit calculations
  // use the tab-scoped values without the overlay needing to re-implement pricing logic.
  const adminSettings = useMemo(() => {
    if (!adminSettingsOverride) return baseAdminSettings;
    return {
      ...baseAdminSettings,
      ...adminSettingsOverride,
      priceOverrides: { ...(baseAdminSettings.priceOverrides || {}), ...(adminSettingsOverride.priceOverrides || {}) },
      deviceMargins: { ...(baseAdminSettings.deviceMargins || {}), ...(adminSettingsOverride.deviceMargins || {}) },
      deviceExtraProfits: { ...(baseAdminSettings.deviceExtraProfits || {}), ...(adminSettingsOverride.deviceExtraProfits || {}) },
    };
  }, [baseAdminSettings, adminSettingsOverride]);
  const sfSettings = useStorefrontSettings();
  // Merge per-tab hidden combos with global hidden combos. Used by every chip
  // visibility filter below so a key hidden in either source disappears.
  const mergedHiddenCombos = useMemo<Record<string, boolean>>(() => {
    const base = (sfSettings.hiddenPriceCombos || {}) as Record<string, boolean>;
    if (!hiddenCombosOverride || Object.keys(hiddenCombosOverride).length === 0) return base;
    return { ...base, ...hiddenCombosOverride };
  }, [sfSettings.hiddenPriceCombos, hiddenCombosOverride]);
  const conditionGuide = useMemo(() => loadConditionGuide(), [conditionGuideRev]);
  const getGradeDescription = useCallback((grade: string) => {
    const entry = conditionGuide.find(e => e.grade === grade);
    if (!entry) return "";
    return entry.description.map(d => typeof d === "string" ? d : d.text).join(" • ");
  }, [conditionGuide]);
  const storefrontDisabledCategories = useSwapStore(s => s.settings.storefrontDisabledCategories);
  const shippingOptions = useMemo(() => {
    if (shippingOptionsOverride) return shippingOptionsOverride;
    if (selectedCategory && sfSettings.shippingDisabledCategories.includes(selectedCategory)) return [];
    return getEnabledShippingOptions(selectedCategory || undefined);
  }, [selectedCategory, sfSettings.shippingDisabledCategories, shippingRev, shippingOptionsOverride]);
  const orderedGrades = useMemo(() => getOrderedGrades(), [adminSettings]);
  const allCategoryLabels = useMemo(() => getCategoryLabels(), [adminSettings]);
  const allCategoryOrder = useMemo(
    () => getCategoryOrder().filter(c => !(storefrontDisabledCategories || []).includes(c)),
    [storefrontDisabledCategories, adminSettings]
  );

  const modelsForCategory = useMemo(() => {
    if (!selectedCategory) return [];
    return allDevices
      .filter(d => d.category === selectedCategory && getDeviceSettings(d.name).enabled)
      .sort((a, b) => {
        const sa = getDeviceSettings(a.name).sortOrder || 0;
        const sb = getDeviceSettings(b.name).sortOrder || 0;
        return sa - sb;
      });
  }, [selectedCategory, allDevices]);

  const device: DeviceModel | undefined = useMemo(() => allDevices.find(d => d.name === selectedModel), [selectedModel, allDevices]);
  const deviceSettings = useMemo(() => device ? getDeviceSettings(device.name) : null, [device]);
  const deviceVariants = useMemo(() => {
    if (!device) return [];
    return getDeviceVariants(device).filter(v => isVariantEnabled(device.name, v.label));
  }, [device]);
  const variantConfig = useMemo(() => device ? getVariantConfig(device.category) : "storage", [device]);
  const variant = useMemo(() => deviceVariants.find(v => v.label === selectedVariant), [deviceVariants, selectedVariant]);

  // Category-level settings
  const categoryModelLabel = useMemo(() => selectedCategory ? getModelLabel(selectedCategory) : "Device Model", [selectedCategory]);
  const categoryGradeLabel = useMemo(() => selectedCategory ? getGradeLabel(selectedCategory) : "Condition Grade", [selectedCategory]);
  const categoryGradesEnabled = useMemo(() => selectedCategory ? areGradesEnabled(selectedCategory) : true, [selectedCategory]);
  const selectedCategorySettings = useMemo(() => selectedCategory ? getCategorySettings(selectedCategory) : null, [selectedCategory]);
  const categoryNoteText = selectedCategorySettings?.noteText?.trim() || "";

  // Use storefront settings for CTA
  const contactNoteEnabled = sfSettings.contactNoteEnabled;
  const contactNoteHeading = sfSettings.contactNoteHeading || "Can't find the device you're looking for?";
  const contactNoteCtaLabel = sfSettings.contactNoteCtaLabel || "Contact us on WhatsApp";
  const hasCategoryNoteBlock = contactNoteEnabled && Boolean(categoryNoteText || contactNoteHeading);

  // Auto-select first variant when config is "none" or only one variant available
  useEffect(() => {
    if (device && deviceVariants.length > 0 && !selectedVariant) {
      if (variantConfig === "none" || deviceVariants.length === 1) {
        setSelectedVariant(deviceVariants[0].label);
      }
    }
  }, [variantConfig, device, deviceVariants, selectedVariant]);

  const effectiveScreenConds = useMemo(() => {
    if (!deviceSettings || !device) return [];
    return getEffectiveScreenConditions(device.category, device.name, deviceSettings.subconditionsEnabled);
  }, [deviceSettings, device]);

  const showSubconditions = effectiveScreenConds.length > 0;

  const deviceColors = useMemo(() => deviceSettings?.colors || [], [deviceSettings]);

  const enabledSubVariants = useMemo(() => {
    if (!device) return [];
    return getEnabledSubVariantsForDevice(device.category, device.name).filter(sv => sv.options.length >= 1);
  }, [device, adminSettings]);

  const enabledGrades = useMemo(() => {
    if (!categoryGradesEnabled) return [];
    if (!deviceSettings) return orderedGrades;
    const baseGrades = orderedGrades.filter(g => deviceSettings.enabledGrades.includes(g));
    // Filter out grades where ALL screen conditions are hidden for current device+variant
    if (!device || !variant) return baseGrades;
    const hiddenCombos = mergedHiddenCombos;
    const screenConds = effectiveScreenConds.length > 0 ? effectiveScreenConds : [undefined];
    return baseGrades.filter(g => {
      return screenConds.some(sc => {
        const key = getPriceKey(device.name, variant.label, g, sc as ScreenCondition | undefined);
        // Check both base key and sub-variant-suffixed keys
        if (!hiddenCombos[key]) return true;
        // If base key is hidden, check if any sub-variant combo is NOT hidden
        if (enabledSubVariants.length > 0) {
          return enabledSubVariants.some(sv => 
            sv.options.some(opt => {
              const fullKey = `${key}|${opt}`;
              return !hiddenCombos[fullKey];
            })
          );
        }
        return false;
      });
    });
  }, [deviceSettings, orderedGrades, categoryGradesEnabled, device, variant, mergedHiddenCombos, effectiveScreenConds, enabledSubVariants]);

  useEffect(() => {
    if (effectiveScreenConds.length === 1) {
      setSelectedScreen(effectiveScreenConds[0] as any);
    }
  }, [effectiveScreenConds]);

  // Auto-select first option of each sub-variant so pricing key is correct immediately
  useEffect(() => {
    if (enabledSubVariants.length === 0) return;
    setSelectedSubVariants(prev => {
      const next = { ...prev };
      let changed = false;
      for (const sv of enabledSubVariants) {
        if (!next[sv.id] && sv.options.length > 0) {
          next[sv.id] = sv.options[0];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [enabledSubVariants]);

  const displayCurrency: DisplayCurrency = adminSettings.displayCurrency || "GHS";
  // Phase B — Tenant scope overlays: if a tenant is active, resolve its
  // configured WhatsApp number for orders, else fall back to admin settings.
  const openWa = useRoutedWhatsAppOpener("orders_cta", adminSettings.whatsappNumber || "233598429052");
  const lockdown = useTenantLockdown();


  const subVariantSuffix = useMemo(() => {
    if (enabledSubVariants.length === 0) return "";
    return enabledSubVariants.map(sv => selectedSubVariants[sv.id] || "").filter(Boolean).join("|");
  }, [enabledSubVariants, selectedSubVariants]);

  useEffect(() => {
    if (enabledGrades.length > 0 && !enabledGrades.includes(selectedGrade)) {
      setSelectedGrade(enabledGrades[0]);
    }
  }, [enabledGrades, selectedGrade]);

  // Variants whose every (grade × screen × sub-variant) combo is hidden are
  // dropped from the chip row entirely — same logic the LiteOverlay applies.
  const visibleDeviceVariants = useMemo(() => {
    if (!device) return deviceVariants;
    const hidden = mergedHiddenCombos;
    if (Object.keys(hidden).length === 0) return deviceVariants;
    const grades = enabledGrades.length > 0 ? enabledGrades : ["Default"];
    const screens = effectiveScreenConds.length > 0 ? effectiveScreenConds : [undefined];
    const svCombos: string[][] = enabledSubVariants.length > 0
      ? enabledSubVariants.reduce<string[][]>(
          (acc, sv) => acc.flatMap(prefix => sv.options.map(o => [...prefix, o])),
          [[]],
        )
      : [[]];
    return deviceVariants.filter(v =>
      grades.some(g => screens.some(sc => svCombos.some(combo => {
        const baseKey = getPriceKey(device.name, v.label, g, sc as ScreenCondition | undefined);
        const fullKey = combo.length ? `${baseKey}|${combo.join("|")}` : baseKey;
        return !hidden[fullKey];
      })))
    );
  }, [device, deviceVariants, mergedHiddenCombos, enabledGrades, effectiveScreenConds, enabledSubVariants]);

  // Re-snap selected variant when it becomes fully hidden (e.g. tab change).
  useEffect(() => {
    if (!selectedVariant || visibleDeviceVariants.length === 0) return;
    if (!visibleDeviceVariants.some(v => v.label === selectedVariant)) {
      setSelectedVariant(visibleDeviceVariants[0].label);
    }
  }, [visibleDeviceVariants, selectedVariant]);

  // Helper: build all sub-variant suffix combinations
  const allSvSuffixes = useMemo<string[]>(() => {
    if (enabledSubVariants.length === 0) return [""];
    return enabledSubVariants.map(sv => sv.options).reduce<string[]>(
      (acc, opts) => acc.flatMap(p => opts.map(o => p ? `${p}|${o}` : o)),
      [""],
    );
  }, [enabledSubVariants]);

  // Re-snap selectedScreen only if it's hidden at EVERY enabled grade.
  // Otherwise we keep it visible and switch grades instead (see next effect).
  useEffect(() => {
    if (!device || !variant || !showSubconditions) return;
    const hidden = mergedHiddenCombos;
    const grades = categoryGradesEnabled
      ? (enabledGrades.length > 0 ? enabledGrades : orderedGrades)
      : ["Default"];
    const visibleAnywhere = effectiveScreenConds.filter(sc =>
      grades.some(g => {
        const key = getPriceKey(device.name, variant.label, g, sc as ScreenCondition);
        return allSvSuffixes.some(suf => !hidden[suf ? `${key}|${suf}` : key]);
      })
    );
    if (visibleAnywhere.length > 0 && !visibleAnywhere.includes(selectedScreen)) {
      setSelectedScreen(visibleAnywhere[0] as ScreenCondition);
    }
  }, [device, variant, showSubconditions, mergedHiddenCombos, categoryGradesEnabled, enabledGrades, orderedGrades, effectiveScreenConds, allSvSuffixes, selectedScreen]);

  // When current grade hides the chosen screen, jump to the first grade that
  // exposes it so the chip selection stays consistent.
  useEffect(() => {
    if (!device || !variant || !categoryGradesEnabled || enabledGrades.length === 0 || !showSubconditions) return;
    const hidden = mergedHiddenCombos;
    const isVisibleAt = (g: string) => {
      const key = getPriceKey(device.name, variant.label, g, selectedScreen as ScreenCondition);
      return allSvSuffixes.some(suf => !hidden[suf ? `${key}|${suf}` : key]);
    };
    if (!isVisibleAt(selectedGrade)) {
      const next = enabledGrades.find(isVisibleAt);
      if (next) setSelectedGrade(next);
    }
  }, [selectedScreen, device, variant, categoryGradesEnabled, enabledGrades, selectedGrade, mergedHiddenCombos, allSvSuffixes, showSubconditions]);

  // Re-snap any sub-variant whose currently-selected option is hidden across
  // every (grade × screen) combo.
  useEffect(() => {
    if (!device || !variant || enabledSubVariants.length === 0) return;
    const hidden = mergedHiddenCombos;
    const grades = categoryGradesEnabled
      ? (enabledGrades.length > 0 ? enabledGrades : orderedGrades)
      : ["Default"];
    const screens = showSubconditions ? effectiveScreenConds : [undefined as any];
    setSelectedSubVariants(prev => {
      let changed = false;
      const next = { ...prev };
      for (const sv of enabledSubVariants) {
        const visible = sv.options.filter(opt => {
          return grades.some(g => screens.some(sc => {
            const baseKey = getPriceKey(device.name, variant.label, g, sc as ScreenCondition | undefined);
            const suffix = enabledSubVariants
              .map(s => s.id === sv.id ? opt : (next[s.id] || s.options[0] || ""))
              .filter(Boolean).join("|");
            const fullKey = suffix ? `${baseKey}|${suffix}` : baseKey;
            return !hidden[fullKey];
          }));
        });
        if (next[sv.id] && !visible.includes(next[sv.id])) {
          next[sv.id] = visible[0] || sv.options[0] || "";
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [device, variant, enabledSubVariants, mergedHiddenCombos, categoryGradesEnabled, enabledGrades, orderedGrades, showSubconditions, effectiveScreenConds]);

  const priceKey = useMemo(() => {
    if (!device || !variant) return "";
    const gradeToUse = categoryGradesEnabled ? selectedGrade : "Default";
    const base = getPriceKey(device.name, variant.label, gradeToUse, showSubconditions ? selectedScreen : undefined);
    return subVariantSuffix ? `${base}|${subVariantSuffix}` : base;
  }, [device, variant, selectedGrade, selectedScreen, showSubconditions, subVariantSuffix, categoryGradesEnabled]);

  const mediaKey = useMemo(() => {
    if (!device) return "";
    const gradeToUse = categoryGradesEnabled ? selectedGrade : "Default";
    return getPriceKey(device.name, "all", gradeToUse, showSubconditions ? selectedScreen : undefined);
  }, [device, selectedGrade, selectedScreen, showSubconditions, categoryGradesEnabled]);

  useEffect(() => {
    onMediaKeyChange?.(mediaKey);
  }, [mediaKey, onMediaKeyChange]);

  const stockKey = useMemo(() => {
    if (!priceKey) return "";
    const colorSuffix = selectedColor ? `|${selectedColor}` : "";
    return `${priceKey}${colorSuffix}`;
  }, [priceKey, selectedColor]);

  const stockStatus = useMemo(() => stockKey ? getStockStatus(stockKey) : "untracked", [stockKey]);
  const outOfStock = stockStatus === "out_of_stock";

  // Performance based commission: staffCommissionPercent% × finalPrice
  // This is NOT the auto EP-based commission — it's the per-user amount set in admin
  // The storefront price = basePrice + performanceCommission so the staff earns from the markup

  const selectedShippingOption = useMemo(() => {
    if (!selectedShipping && shippingOptions.length > 0) return shippingOptions[0];
    return shippingOptions.find(o => o.id === selectedShipping) || shippingOptions[0] || null;
  }, [selectedShipping, shippingOptions]);

  useEffect(() => {
    const key = "khays_selected_shipping_preview";
    if (selectedShipping && selectedShippingOption) {
      localStorage.setItem(key, JSON.stringify(selectedShippingOption));
    } else {
      localStorage.removeItem(key);
    }
    window.dispatchEvent(new CustomEvent("khays-shipping-preview-change"));
  }, [selectedShipping, selectedShippingOption]);

  // When shipping is disabled for this category, fee is 0
  const shippingFee = shippingOptions.length === 0 ? 0 : (selectedShippingOption?.fee ?? adminSettings.shippingFee ?? 500);

  const { finalPrice, isUnavailable, displayPrice } = useMemo(() => {
    if (!variant || !device) return { finalPrice: 0, isUnavailable: false, displayPrice: 0 };
    if (isVariantEnquiryOnly(device.name, variant.label)) return { finalPrice: 0, isUnavailable: true, displayPrice: 0 };
    const screenCond = showSubconditions ? selectedScreen : undefined;
    const gradeToUse = categoryGradesEnabled ? selectedGrade : "Default";
    const key = getPriceKey(device.name, variant.label, gradeToUse, screenCond);
    const hiddenCombos = mergedHiddenCombos;
    const fullKey = subVariantSuffix ? `${key}|${subVariantSuffix}` : key;
    // Check if the specific combo (with sub-variant) or the base combo is hidden
    if (hiddenCombos[fullKey] || hiddenCombos[key]) return { finalPrice: 0, isUnavailable: true, displayPrice: 0 };
    const overridePrice = adminSettings.priceOverrides[fullKey] ?? adminSettings.priceOverrides[key];
    const basePrice = overridePrice ?? variant.price;
    if (basePrice <= 0) return { finalPrice: 0, isUnavailable: true, displayPrice: 0 };
    const deviceExtraProfit = adminSettings.deviceExtraProfits?.[fullKey] ?? adminSettings.deviceExtraProfits?.[key] ?? 0;
    const deviceMargin = adminSettings.deviceMargins?.[fullKey] ?? adminSettings.deviceMargins?.[key] ?? null;
    const marginToUse = deviceMargin !== null ? deviceMargin : adminSettings.marginPercent;
    const price = calculateFinalPrice(basePrice, marginToUse, rate, device.usesYuanPricing, deviceExtraProfit);
    if (price <= 0) return { finalPrice: 0, isUnavailable: true, displayPrice: 0 };
    // Performance based commission: staff's commission_percent × finalPrice / 100
    const perfCommission = referralStaff && staffCommissionPercent > 0 ? Math.round(price * staffCommissionPercent / 100) : 0;
    const priceWithCommission = price + perfCommission;
    const converted = convertFromGHS(priceWithCommission, displayCurrency, usdRate, rate);
    return { finalPrice: priceWithCommission, isUnavailable: false, displayPrice: converted };
  }, [variant, device, selectedGrade, selectedScreen, showSubconditions, rate, usdRate, adminSettings, displayCurrency, referralStaff, staffCommissionPercent, subVariantSuffix, categoryGradesEnabled]);

  const totalPrice = finalPrice + shippingFee;

  const storageRef = useRef<HTMLDivElement>(null);

  const scrollToNext = useCallback((ref?: React.RefObject<HTMLDivElement | null>) => {
    setTimeout(() => {
      const target = ref?.current;
      if (!target) return;
      const rect = target.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      if (rect.top > viewportHeight * 0.75) {
        window.scrollBy({ top: rect.top - viewportHeight * 0.4, behavior: "smooth" });
      }
    }, 200);
  }, []);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.category) setSelectedCategory(detail.category as DeviceCategory);
      if (detail?.device) setSelectedModel(detail.device);
      if (detail?.variant) setSelectedVariant(detail.variant);
      // Scroll to the price section so the visitor can proceed directly
      setTimeout(() => {
        if (priceRef.current) {
          priceRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        } else if (formRef.current) {
          formRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 300);
    };
    window.addEventListener("select-device", handler);
    return () => window.removeEventListener("select-device", handler);
  }, []);

  const handleCategoryChange = (cat: string) => {
    setSelectedCategory(cat as DeviceCategory);
    setSelectedModel(""); setSelectedVariant(""); setSelectedGrade("Grade A"); setSelectedScreen("Original Screen"); setSelectedColor(""); setSelectedSubVariants({});
    scrollToNext();
  };
  const handleModelChange = (model: string) => {
    setSelectedModel(model); setSelectedVariant(""); setSelectedGrade("Grade A"); setSelectedScreen("Original Screen"); setSelectedColor(""); setSelectedSubVariants({});
    scrollToNext();
  };

  const deviceDisplayName = device ? getDeviceDisplayName(device.name) : "";

  const handleCheckout = () => {
    if (!device || !variant || !customerName.trim() || !customerLocation.trim() || isUnavailable || outOfStock) return;
    if (!customerPhone.trim()) return;
    // Phase B — tenant lockdown gate for checkout / new orders.
    if (lockdown.checkout_disabled || lockdown.orders_paused) {
      toast({
        title: lockdown.orders_paused ? "Orders are paused" : "Checkout disabled",
        description: lockdown.message || "This store is not accepting new orders right now.",
        variant: "destructive",
      });
      return;
    }
    // ── Soft auth gate: require sign-in on Buy when enabled ──
    if (requireAuthForBuy()) return;

    // Save returning customer info
    saveReturningCustomer(customerName.trim(), customerLocation.trim(), customerPhone.trim()); if (authUser?.id) backfillCustomerProfile({ userId: authUser.id, email: authUser.email, fullName: customerName.trim(), phoneNumber: customerPhone.trim(), location: customerLocation.trim() }).then(r => { if (r?.written?.length) toast({ title: "Profile updated", description: `Saved ${r.written.join(", ")} to your account.` }); });
    const newOrder = addOrder({
      deviceName: device.name, variant: variant.label, grade: categoryGradesEnabled ? selectedGrade : undefined,
      screenType: showSubconditions ? selectedScreen : undefined, finalPrice,
      color: selectedColor || undefined,
      customerName: customerName.trim(), location: customerLocation.trim(),
      smsOptIn: true, customerPhone: customerPhone.trim(),
      referredBy: referralStaff,
      shippingFee, shippingLabel: selectedShippingOption?.label, shippingOptionId: selectedShippingOption?.id,
      orderSource: referralStaff ? "referral" : "storefront",
      customerUserId: authUser?.id,
      customerEmail: authUser?.email || undefined,
    });
    const trackingUrl = `${window.location.origin}/track?id=${newOrder.invoiceId}`;
    const messageLines = [
      "I want to pre-order:",
      categoryGradesEnabled ? selectedGrade : "",
      `${deviceDisplayName} ${variant.label}`,
      showSubconditions ? `Screen: ${selectedScreen}` : "",
      ...enabledSubVariants
        .filter(sv => selectedSubVariants[sv.id])
        .map(sv => `${sv.name}: ${selectedSubVariants[sv.id]}`),
      selectedColor ? `Colour: ${selectedColor}` : "",
      `Device Price: ${formatPrice(finalPrice)}`,
      shippingFee > 0 && selectedShippingOption ? `Shipping: ${selectedShippingOption.label} (${formatPrice(selectedShippingOption.fee)})` : "",
      shippingFee > 0 ? `Total (incl. shipping): ${formatPrice(totalPrice)}` : "",
      "",
      `Name: ${customerName}`,
      `Location: ${customerLocation}`,
      `Phone: ${customerPhone}`,
      referralStaff ? `Referred by: ${referralStaff}` : "",
      "",
      `📋 Invoice: ${newOrder.invoiceId}`,
      `📦 Track: ${trackingUrl}`,
    ].filter(Boolean).join("\n");
    openWa(messageLines);
  };

  const handleEnquiry = () => {
    if (!customerName.trim() || !customerLocation.trim() || !customerPhone.trim()) return;
    saveReturningCustomer(customerName.trim(), customerLocation.trim(), customerPhone.trim()); if (authUser?.id) backfillCustomerProfile({ userId: authUser.id, email: authUser.email, fullName: customerName.trim(), phoneNumber: customerPhone.trim(), location: customerLocation.trim() }).then(r => { if (r?.written?.length) toast({ title: "Profile updated", description: `Saved ${r.written.join(", ")} to your account.` }); });
    addEnquiry({
      deviceName: device?.name || "General Enquiry", variant: variant?.label || "", grade: categoryGradesEnabled && device ? selectedGrade : "",
      screenType: showSubconditions ? selectedScreen : undefined,
      color: selectedColor || undefined,
      customerName: customerName.trim(), location: customerLocation.trim(),
      customerPhone: customerPhone.trim(), referredBy: referralStaff,
      orderSource: "enquiry",
    });
    const deviceInfo = device ? `${deviceDisplayName}${variant ? ` ${variant.label}` : ""}` : "";
    const messageLines = [
      "I want to enquire about",
      deviceInfo ? `Device: ${deviceInfo}` : "",
      ...enabledSubVariants
        .filter(sv => selectedSubVariants[sv.id])
        .map(sv => `${sv.name}: ${selectedSubVariants[sv.id]}`),
      selectedColor ? `Colour: ${selectedColor}` : "",
      "",
      `Name: ${customerName.trim()}`,
      `Location: ${customerLocation.trim()}`,
      `WhatsApp: ${customerPhone.trim()}`,
    ].filter(Boolean).join("\n");
    openWa(messageLines);
  };

  const handleOutOfStockEnquiry = () => {
    if (!device || !variant || !customerName.trim() || !customerLocation.trim() || !customerPhone.trim()) return;
    saveReturningCustomer(customerName.trim(), customerLocation.trim(), customerPhone.trim()); if (authUser?.id) backfillCustomerProfile({ userId: authUser.id, email: authUser.email, fullName: customerName.trim(), phoneNumber: customerPhone.trim(), location: customerLocation.trim() }).then(r => { if (r?.written?.length) toast({ title: "Profile updated", description: `Saved ${r.written.join(", ")} to your account.` }); });
    addEnquiry({
      deviceName: device.name, variant: variant.label, grade: categoryGradesEnabled ? selectedGrade : "",
      screenType: showSubconditions ? selectedScreen : undefined,
      color: selectedColor || undefined,
      customerName: customerName.trim(), location: customerLocation.trim(),
      customerPhone: customerPhone.trim(), referredBy: referralStaff,
      orderSource: "enquiry",
    });
    const messageLines = [
      "I'm interested in a currently out-of-stock item:",
      `${deviceDisplayName} ${variant.label}`,
      categoryGradesEnabled ? selectedGrade : "",
      selectedColor ? `Colour: ${selectedColor}` : "",
      "Please notify me when available.",
      "",
      `Name: ${customerName.trim()}`,
      `Location: ${customerLocation.trim()}`,
      `WhatsApp: ${customerPhone.trim()}`,
    ].filter(Boolean).join("\n");
    openWa(messageLines);
  };

  // Inquiry popup - opens dialog, prefills from returning customer or main form
  const openInquiryPopup = () => {
    setInquiryName(customerName);
    setInquiryLocation(customerLocation);
    setInquiryPhone(customerPhone);
    setInquiryOpen(true);
  };

  const handleInquirySubmit = () => {
    if (!inquiryName.trim() || !inquiryLocation.trim() || !inquiryPhone.trim() || !isValidPhoneNumber(inquiryPhone)) return;
    saveReturningCustomer(inquiryName.trim(), inquiryLocation.trim(), inquiryPhone.trim()); if (authUser?.id) backfillCustomerProfile({ userId: authUser.id, email: authUser.email, fullName: inquiryName.trim(), phoneNumber: inquiryPhone.trim(), location: inquiryLocation.trim() }).then(r => { if (r?.written?.length) toast({ title: "Profile updated", description: `Saved ${r.written.join(", ")} to your account.` }); });
    // Also update main form fields
    setCustomerName(inquiryName.trim());
    setCustomerLocation(inquiryLocation.trim());
    setCustomerPhone(inquiryPhone.trim());
    
    addEnquiry({
      deviceName: device?.name || "Price Inquiry", variant: variant?.label || "", grade: categoryGradesEnabled && device ? selectedGrade : "",
      screenType: showSubconditions ? selectedScreen : undefined,
      color: selectedColor || undefined,
      customerName: inquiryName.trim(), location: inquiryLocation.trim(),
      customerPhone: inquiryPhone.trim(), referredBy: referralStaff,
      orderSource: "enquiry",
    });
    const deviceInfo = device ? `${deviceDisplayName}${variant ? ` ${variant.label}` : ""}` : "";
    const messageLines = [
      "Hi, I'd like to inquire about a special price.",
      deviceInfo ? `Device: ${deviceInfo}` : "",
      categoryGradesEnabled && device ? selectedGrade : "",
      ...enabledSubVariants
        .filter(sv => selectedSubVariants[sv.id])
        .map(sv => `${sv.name}: ${selectedSubVariants[sv.id]}`),
      selectedColor ? `Colour: ${selectedColor}` : "",
      variant && !isUnavailable ? `Listed Price: ${formatPrice(displayPrice, displayCurrency)}` : "",
      "",
      `Name: ${inquiryName.trim()}`,
      `Location: ${inquiryLocation.trim()}`,
      `WhatsApp: ${inquiryPhone.trim()}`,
    ].filter(Boolean).join("\n");
    openWa(messageLines);
    setInquiryOpen(false);
  };

  // Auto-detect returning customer by phone
  const handlePhoneBlur = () => {
    if (customerPhone.trim() && isValidPhoneNumber(customerPhone) && !customerName.trim()) {
      const prev = findPreviousCustomerByPhone(customerPhone);
      if (prev) {
        setCustomerName(prev.name);
        setCustomerLocation(prev.location);
        setIsReturning(true);
        toast({ title: "Welcome back!", description: `We found your previous info: ${prev.name}` });
      }
    }
  };

  const handleCategoryNoteWhatsApp = () => {
    if (!selectedCategory) return;
    // Open the inquiry popup instead of sending directly
    setInquiryName(customerName);
    setInquiryLocation(customerLocation);
    setInquiryPhone(customerPhone);
    setContactInquiryMode(true);
    setInquiryOpen(true);
  };

  const handleContactInquirySubmit = () => {
    if (!inquiryName.trim() || !inquiryLocation.trim() || !inquiryPhone.trim() || !isValidPhoneNumber(inquiryPhone)) return;
    saveReturningCustomer(inquiryName.trim(), inquiryLocation.trim(), inquiryPhone.trim()); if (authUser?.id) backfillCustomerProfile({ userId: authUser.id, email: authUser.email, fullName: inquiryName.trim(), phoneNumber: inquiryPhone.trim(), location: inquiryLocation.trim() }).then(r => { if (r?.written?.length) toast({ title: "Profile updated", description: `Saved ${r.written.join(", ")} to your account.` }); });
    setCustomerName(inquiryName.trim());
    setCustomerLocation(inquiryLocation.trim());
    setCustomerPhone(inquiryPhone.trim());

    addEnquiry({
      deviceName: device?.name || "Contact Inquiry",
      variant: variant?.label || "",
      grade: categoryGradesEnabled && device ? selectedGrade : "",
      screenType: showSubconditions ? selectedScreen : undefined,
      color: selectedColor || undefined,
      customerName: inquiryName.trim(),
      location: inquiryLocation.trim(),
      customerPhone: inquiryPhone.trim(),
      referredBy: referralStaff,
      orderSource: "enquiry",
    });

    const categoryLabel = allCategoryLabels[selectedCategory as string] || selectedCategory;
    const selectedModelName = device ? getDeviceDisplayName(device.name) : "";
    const baseTemplate = sfSettings.contactNoteWhatsAppMessage || "Hi, I'm interested in devices from {category}.";
    const message = baseTemplate
      .replace(/\{category\}/gi, categoryLabel)
      .replace(/\{model\}/gi, selectedModelName || "")
      .replace(/\{device\}/gi, selectedModelName || "")
      .replace(/\n{3,}/g, "\n\n")
      .trim()
      + `\n\nName: ${inquiryName.trim()}\nLocation: ${inquiryLocation.trim()}\nPhone: ${inquiryPhone.trim()}`;
    openWa(message);
    setInquiryOpen(false);
    setContactInquiryMode(false);
  };

  const phoneValid = isValidPhoneNumber(customerPhone);
  const inquiryPhoneValid = isValidPhoneNumber(inquiryPhone);
  const isFormComplete = device && variant && customerName.trim() && customerLocation.trim() && !isUnavailable && !outOfStock && customerPhone.trim() && phoneValid;
  const canEnquire = customerName.trim() && customerLocation.trim() && customerPhone.trim() && phoneValid;
  const storageLabel = device ? getStorageLabel(device.category) : "Storage";

  return (
    <div ref={formRef} className="w-full max-w-lg mx-auto animate-slide-up space-y-4">
      {!compact && <ConditionGuide />}
      {!compact && <DeviceMediaGallery deviceName={device?.name} category={(device?.category || selectedCategory || undefined) as any} conditionLabel={variant ? selectedGrade : undefined} mediaKey={mediaKey} />}
      <div className={compact ? "space-y-5" : "bg-card rounded-2xl shadow-card p-5 md:p-7 space-y-5 border border-border/50"}>
        {!compact && <h2 className="font-display text-lg font-bold text-card-foreground text-center">Configure Your Device</h2>}

        {referralStaff && (
          <div className="bg-accent/50 rounded-lg p-2.5 text-center border border-accent-foreground/10">
            <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
              <Tag className="w-3 h-3" /> Referred by <span className="font-semibold text-foreground">{referralStaff}</span>
            </p>
          </div>
        )}

        <AdSlotRenderer position="inline-form-top" />

        {/* Category */}
        {!compact && <div className="space-y-1.5">
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Device Category</label>
          <div className="relative">
            <select value={selectedCategory} onChange={e => handleCategoryChange(e.target.value)} className="w-full appearance-none bg-muted text-foreground rounded-xl px-4 py-3 pr-10 text-sm font-medium border border-border focus:ring-2 focus:ring-ring focus:outline-none transition-all">
              <option value="">Select a category...</option>
              {allCategoryOrder.map(cat => <option key={cat} value={cat}>{allCategoryLabels[cat] || cat}</option>)}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          </div>
        </div>}

        {/* Model */}
        {!compact && selectedCategory && (
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{categoryModelLabel}</label>
            <div className="relative">
              <select value={selectedModel} onChange={e => handleModelChange(e.target.value)} className="w-full appearance-none bg-muted text-foreground rounded-xl px-4 py-3 pr-10 text-sm font-medium border border-border focus:ring-2 focus:ring-ring focus:outline-none transition-all">
                <option value="">Select...</option>
                {modelsForCategory.map(d => <option key={d.name} value={d.name}>{getDeviceDisplayName(d.name)}</option>)}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            </div>
          </div>
        )}

        {/* Contact note + WhatsApp CTA */}
        {!compact && selectedCategory && hasCategoryNoteBlock && (
          <div className="bg-accent/30 rounded-xl p-3 border border-accent-foreground/10 space-y-2">
            {contactNoteHeading && (
              <p className="text-xs font-semibold text-foreground leading-relaxed"
                style={sfSettings.contactNoteHeadingColor ? { color: sfSettings.contactNoteHeadingColor } : undefined}>
                {contactNoteHeading}
              </p>
            )}
            {categoryNoteText && <p className="text-xs text-foreground leading-relaxed">{categoryNoteText}</p>}
            <button onClick={handleCategoryNoteWhatsApp}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline"
              style={sfSettings.contactNoteCtaLabelColor ? { color: sfSettings.contactNoteCtaLabelColor } : undefined}>
              <MessageCircle className="w-3.5 h-3.5" />{contactNoteCtaLabel}
            </button>
          </div>
        )}

        {/* Variant - hidden when "none" */}
        {device && variantConfig !== "none" && (
          <div ref={storageRef} className="space-y-1.5">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{storageLabel}</label>
            <div className="relative">
              <select value={selectedVariant} onChange={e => setSelectedVariant(e.target.value)} className="w-full appearance-none bg-muted text-foreground rounded-xl px-4 py-3 pr-10 text-sm font-medium border border-border focus:ring-2 focus:ring-ring focus:outline-none transition-all">
                <option value="">Select {storageLabel.toLowerCase()}...</option>
                {visibleDeviceVariants.map(v => <option key={v.label} value={v.label}>{v.label}</option>)}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            </div>
          </div>
        )}

        {/* Sub-Variants */}
        {(variant || (device && variantConfig === "none")) && enabledSubVariants.map(sv => {
          // Show options that are visible at any (grade × screen) combo so the
          // customer can see all configurations that exist on the device.
          const hiddenCombos = mergedHiddenCombos;
          const grades = categoryGradesEnabled
            ? (enabledGrades.length > 0 ? enabledGrades : orderedGrades)
            : ["Default"];
          const screens = showSubconditions ? effectiveScreenConds : [undefined as any];
          const visibleOptions = sv.options.filter(opt => {
            if (!device) return true;
            return grades.some(g => screens.some(sc => {
              const baseKey = getPriceKey(device.name, variant?.label || "", g, sc as ScreenCondition | undefined);
              const testSuffix = enabledSubVariants.map(s => s.id === sv.id ? opt : (selectedSubVariants[s.id] || s.options[0] || "")).filter(Boolean).join("|");
              const testKey = testSuffix ? `${baseKey}|${testSuffix}` : baseKey;
              return !hiddenCombos[testKey];
            }));
          });
          if (visibleOptions.length === 0) return null;
          return (
            <div key={sv.id} className="space-y-1.5">
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                <Settings2 className="w-3 h-3" />{sv.name}
              </label>
              {visibleOptions.length <= 4 ? (
                <div className="flex flex-wrap gap-2">
                  {visibleOptions.map(opt => (
                    <button key={opt} onClick={() => setSelectedSubVariants(prev => ({ ...prev, [sv.id]: prev[sv.id] === opt ? "" : opt }))}
                      className={`px-3 py-2 rounded-xl text-xs font-medium transition-all border-2 ${
                        selectedSubVariants[sv.id] === opt ? "border-primary bg-primary/5 text-foreground" : "border-border bg-muted text-muted-foreground hover:border-primary/30"
                      }`}>{opt}</button>
                  ))}
                </div>
              ) : (
                <div className="relative">
                  <select value={selectedSubVariants[sv.id] || ""} onChange={e => setSelectedSubVariants(prev => ({ ...prev, [sv.id]: e.target.value }))}
                    className="w-full appearance-none bg-muted text-foreground rounded-xl px-4 py-3 pr-10 text-sm font-medium border border-border focus:ring-2 focus:ring-ring focus:outline-none transition-all">
                    <option value="">Select {sv.name.toLowerCase()}...</option>
                    {visibleOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                </div>
              )}
            </div>
          );
        })}

        {/* Color */}
        {variant && deviceColors.length > 0 && (
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1"><Palette className="w-3 h-3" />Colour</label>
            <div className="flex flex-wrap gap-2">
              {deviceColors.map(color => (
                <button key={color.id} onClick={() => setSelectedColor(selectedColor === color.name ? "" : color.name)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium transition-all border-2 ${
                    selectedColor === color.name ? "border-primary bg-primary/5" : "border-border bg-muted hover:border-primary/30"
                  }`}>
                  <span className="w-4 h-4 rounded-full border border-border/50 flex-shrink-0" style={{ backgroundColor: color.hex }} />
                  <span>{color.name}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Grade */}
        {(variantConfig === "none" ? device : variant) && enabledGrades.length > 0 && (() => {
          if (enabledGrades.length === 1) {
            // Auto-select the only visible grade and show as info label
            if (selectedGrade !== enabledGrades[0]) {
              setTimeout(() => setSelectedGrade(enabledGrades[0]), 0);
            }
            return (
              <div className="bg-accent/30 rounded-xl p-3 border border-accent-foreground/10">
                <p className="text-xs text-muted-foreground">{categoryGradeLabel}: <span className="font-semibold text-foreground">{enabledGrades[0]}</span></p>
              </div>
            );
          }
          return (
            <TooltipProvider>
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{categoryGradeLabel}</label>
                {sfSettings.gradeGuideEnabled && (
                  <button type="button" onClick={() => {
                    const el = document.querySelector('[data-condition-guide]');
                    if (el) {
                      // Open the guide if it's closed, then scroll
                      const content = el.querySelector('.animate-slide-up');
                      if (!content) {
                        const btn = el.querySelector('button');
                        if (btn) btn.click();
                      }
                      setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
                    }
                  }} className="text-[10px] text-primary hover:underline italic">
                    click here to know what grades mean
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {enabledGrades.map((g, idx) => {
                  const color = getGradeColor(g, idx);
                  const desc = getGradeDescription(g);
                  return (
                    <div key={g} className="flex items-center gap-1">
                        <button onClick={() => setSelectedGrade(g)} type="button"
                          className="py-2 px-3 rounded-xl text-xs font-semibold transition-all border-2 whitespace-nowrap"
                          style={selectedGrade === g
                            ? { backgroundColor: color.bg, color: color.text, borderColor: color.bg }
                            : { borderColor: 'var(--border)', backgroundColor: 'var(--muted)', color: 'var(--muted-foreground)' }
                          }>
                          {g}
                        </button>
                        {desc && (
                          <Popover>
                            <PopoverTrigger asChild>
                              <button type="button" className="p-1 rounded-full hover:bg-accent transition-colors shrink-0">
                                <HelpCircle className="w-4 h-4 text-muted-foreground" />
                              </button>
                            </PopoverTrigger>
                            <PopoverContent side="top" className="w-56 p-3">
                              <p className="font-semibold text-xs mb-0.5">{g}</p>
                              <p className="text-[10px] text-muted-foreground">{desc}</p>
                            </PopoverContent>
                          </Popover>
                        )}
                    </div>
                  );
                })}
              </div>
            </div>
            </TooltipProvider>
          );
        })()}

        {/* Screen condition */}
        {variant && showSubconditions && (() => {
          const hiddenCombos = mergedHiddenCombos;
          const grades = categoryGradesEnabled
            ? (enabledGrades.length > 0 ? enabledGrades : orderedGrades)
            : ["Default"];
          // A screen condition is visible if it has any visible (grade × sub-variant) combo
          const visibleScreenConds = effectiveScreenConds.filter(sc =>
            grades.some(g => {
              const key = getPriceKey(device!.name, variant.label, g, sc as ScreenCondition);
              return allSvSuffixes.some(suf => !hiddenCombos[suf ? `${key}|${suf}` : key]);
            })
          );
          if (visibleScreenConds.length === 0) return null;
          if (visibleScreenConds.length === 1) {
            // Auto-select the only visible screen condition and show as info label
            if (selectedScreen !== visibleScreenConds[0]) {
              setTimeout(() => setSelectedScreen(visibleScreenConds[0] as ScreenCondition), 0);
            }
            return (
              <div className="bg-accent/30 rounded-xl p-3 border border-accent-foreground/10">
                <p className="text-xs text-muted-foreground">Screen: <span className="font-semibold text-foreground">{visibleScreenConds[0] === "3rd Party Screen" ? `${visibleScreenConds[0]} (TRUE TONE ✅)` : visibleScreenConds[0]}</span></p>
              </div>
            );
          }
          return (
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Screen Condition</label>
              <div className="relative">
                <select value={selectedScreen} onChange={e => setSelectedScreen(e.target.value as ScreenCondition)} className="w-full appearance-none bg-muted text-foreground rounded-xl px-4 py-3 pr-10 text-sm font-medium border border-border focus:ring-2 focus:ring-ring focus:outline-none transition-all">
                  {visibleScreenConds.map(sc => <option key={sc} value={sc}>{sc === "3rd Party Screen" ? `${sc} (TRUE TONE ✅)` : sc}</option>)}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
              </div>
            </div>
          );
        })()}

        {/* Price */}
        {variant && (
          <div ref={priceRef} className="bg-primary/5 rounded-2xl p-5 text-center border border-primary/10">
            {outOfStock ? (
              <div className="space-y-2">
                <div className="flex items-center justify-center gap-2 text-destructive">
                  <PackageX className="w-5 h-5" /><p className="text-lg font-bold">Out of Stock</p>
                </div>
                <p className="text-xs text-muted-foreground">This item is currently unavailable, but you can still enquire below.</p>
              </div>
            ) : isUnavailable ? (
              <div className="flex items-center justify-center gap-2 text-muted-foreground">
                <AlertTriangle className="w-4 h-4" /><p className="text-sm font-medium italic">Currently unavailable — enquire below.</p>
              </div>
            ) : (
              <div>
                <p className="price-tag text-3xl">{formatPrice(displayPrice, displayCurrency)}</p>
                {shippingFee > 0 && (
                  <div className="mt-2">
                    <p className="text-xs text-muted-foreground">+ {formatPrice(shippingFee)} shipping</p>
                    <p className="text-xl font-bold text-foreground mt-1">{formatPrice(totalPrice)} total</p>
                  </div>
                )}
              </div>
            )}
            {hasStockTracking(stockKey) && !outOfStock && stockStatus === "low_stock" && (
              <p className="text-xs text-destructive mt-1 font-medium">⚠️ Low stock — order soon!</p>
            )}
          </div>
        )}
        {variant && <p className="text-[10px] text-center text-muted-foreground italic">Device prices are subject to change due to live exchange rates.</p>}

        {/* Inquiry button - respect global and per-device toggles */}
        {variant && !isUnavailable && !outOfStock && (() => {
          const inquiryEnabled = sfSettings.inquireLowerPriceEnabled !== false;
          const deviceList = sfSettings.inquireLowerPriceDevices || [];
          const showForDevice = deviceList.length === 0 || (device && deviceList.includes(device.name));
          return inquiryEnabled && showForDevice ? (
            <button onClick={openInquiryPopup}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-medium transition-all border border-accent-foreground/10 bg-accent/50 text-muted-foreground hover:bg-accent hover:text-foreground">
              <MessageCircle className="w-4 h-4" /> Want a lower price? Inquire via WhatsApp
            </button>
          ) : null;
        })()}

        {/* Shipping options */}
        {variant && !isUnavailable && !outOfStock && shippingOptions.length > 1 && (
          <div className="bg-accent/30 rounded-xl p-3 border border-accent-foreground/10 space-y-2">
            <p className="text-xs font-semibold text-foreground">{shippingLabel || "Shipping Options"}</p>
            <div className="space-y-1.5">
              {shippingOptions.map(opt => (
                <label key={opt.id} className={`flex items-start gap-2 p-2 rounded-lg cursor-pointer transition-all border ${
                  (selectedShipping === opt.id || (!selectedShipping && opt.id === shippingOptions[0].id)) ? "border-primary bg-primary/5" : "border-transparent hover:bg-accent/30"
                }`}>
                  <input type="radio" name="shipping" value={opt.id} checked={selectedShipping === opt.id || (!selectedShipping && opt.id === shippingOptions[0].id)}
                    onChange={() => setSelectedShipping(opt.id)} className="mt-0.5 w-3 h-3 min-h-0 min-w-0" />
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-foreground">{opt.label} — {formatPrice(opt.fee)}</p>
                    {opt.description && <p className="text-[10px] text-muted-foreground">{opt.description}</p>}
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Single shipping info */}
        {variant && !isUnavailable && !outOfStock && shippingOptions.length === 1 && (
          <div className="bg-accent/30 rounded-xl p-3 border border-accent-foreground/10">
            <p className="text-xs text-muted-foreground"><span className="font-semibold text-foreground">{shippingOptions[0].label}: {formatPrice(shippingOptions[0].fee)}</span></p>
            {shippingOptions[0].description && <p className="text-[10px] text-muted-foreground mt-0.5">{shippingOptions[0].description}</p>}
          </div>
        )}

        {/* Name & Location - show after variant selected (gradual reveal) */}
        {variant && (
          <div className="space-y-3">
            {isReturning && (
              <div className="bg-primary/5 rounded-lg p-2 border border-primary/10 text-center">
                <p className="text-[10px] text-primary font-medium">👋 Welcome back, {customerName}! Your info has been pre-filled.</p>
              </div>
            )}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Your Name <span className="text-destructive">*</span></label>
              <input type="text" value={customerName} onChange={e => setCustomerName(sanitizeText(e.target.value, 100))} placeholder="Enter your full name" maxLength={100}
                className="w-full bg-muted text-foreground rounded-xl px-4 py-3 text-sm border border-border focus:ring-2 focus:ring-ring focus:outline-none transition-all placeholder:text-muted-foreground/50" />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Delivery Location <span className="text-destructive">*</span></label>
                <button
                  type="button"
                  onClick={async () => {
                    if (!navigator.geolocation) {
                      toast({ title: "Location not supported", description: "Your browser doesn't support geolocation.", variant: "destructive" });
                      return;
                    }
                    setGeoLoading(true);
                    navigator.geolocation.getCurrentPosition(
                      async (pos) => {
                        try {
                          const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${pos.coords.latitude}&lon=${pos.coords.longitude}&format=json&addressdetails=1`, {
                            headers: { "Accept-Language": "en" },
                            signal: AbortSignal.timeout(8000),
                          });
                          if (res.ok) {
                            const data = await res.json();
                            const addr = data.address || {};
                            const location = [addr.city || addr.town || addr.village || addr.suburb, addr.state, addr.country].filter(Boolean).join(", ");
                            const resolved = sanitizeText(location || data.display_name?.split(",").slice(0, 3).join(",") || "", 100);
                            if (resolved) {
                              setCustomerLocation(resolved);
                              toast({ title: "Location found", description: resolved });
                            } else {
                              toast({ title: "Could not determine location", description: "Please enter your location manually.", variant: "destructive" });
                            }
                          } else {
                            toast({ title: "Location lookup failed", description: "Please enter your location manually.", variant: "destructive" });
                          }
                        } catch {
                          toast({ title: "Location lookup failed", description: "Please enter your location manually.", variant: "destructive" });
                        } finally { setGeoLoading(false); }
                      },
                      (err) => {
                        setGeoLoading(false);
                        const msg = err.code === 1 ? "Location permission denied. Please allow location access or enter manually."
                          : err.code === 2 ? "Location unavailable. Please enter manually."
                          : "Location request timed out. Please enter manually.";
                        toast({ title: "Location error", description: msg, variant: "destructive" });
                      },
                      { timeout: 10000, enableHighAccuracy: false }
                    );
                  }}
                  disabled={geoLoading}
                  className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-semibold bg-primary/10 text-primary hover:bg-primary/20 transition-all disabled:opacity-50"
                >
                  {geoLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <MapPin className="w-3 h-3" />}
                  {geoLoading ? "Locating..." : "Use My Location"}
                </button>
              </div>
              <input type="text" value={customerLocation} onChange={e => setCustomerLocation(sanitizeText(e.target.value, 100))} placeholder="City / Town in Ghana" maxLength={100}
                className="w-full bg-muted text-foreground rounded-xl px-4 py-3 text-sm border border-border focus:ring-2 focus:ring-ring focus:outline-none transition-all placeholder:text-muted-foreground/50" />
            </div>
          </div>
        )}

        {/* Phone - show after variant */}
        {variant && (
          <div className="space-y-1.5">
            <div className="flex items-center gap-2 mb-1">
              <Phone className="w-4 h-4 text-primary" />
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">WhatsApp Phone Number <span className="text-destructive">*</span></label>
            </div>
            <p className="text-[10px] text-muted-foreground mb-1">Required for order tracking & delivery updates (minimum 10 digits)</p>
            <input type="tel" value={customerPhone} onChange={e => setCustomerPhone(sanitizePhone(e.target.value, 15))} onBlur={handlePhoneBlur} placeholder="e.g. 0598429052 or +233598429052" maxLength={15}
              className={`w-full bg-muted text-foreground rounded-xl px-4 py-3 text-sm border focus:ring-2 focus:ring-ring focus:outline-none transition-all placeholder:text-muted-foreground/50 ${customerPhone.trim() && !phoneValid ? 'border-destructive' : 'border-border'}`} />
            {customerPhone.trim() && !phoneValid && (
              <p className="text-[10px] text-destructive font-medium">Valid Ghana phone required: exactly 10 digits or 12-13 digits starting with 233</p>
            )}
          </div>
        )}

        {/* Action buttons */}
        {outOfStock ? (
          <div className="space-y-2">
            <button disabled className="w-full flex items-center justify-center gap-2 py-4 rounded-2xl text-base font-bold bg-muted text-muted-foreground cursor-not-allowed">
              <PackageX className="w-5 h-5" /> Out of Stock
            </button>
            <button onClick={handleOutOfStockEnquiry} disabled={!canEnquire}
              className={`w-full flex items-center justify-center gap-2 py-3 rounded-2xl text-sm font-bold transition-all ${canEnquire ? "bg-accent text-foreground hover:bg-accent/80 border border-border" : "bg-muted text-muted-foreground cursor-not-allowed"}`}>
              <MessageCircle className="w-4 h-4" /> Enquire About This Item
            </button>
          </div>
        ) : isUnavailable && variant ? (
          <button onClick={handleEnquiry} disabled={!canEnquire}
            className={`w-full flex items-center justify-center gap-2 py-4 rounded-2xl text-base font-bold transition-all ${canEnquire ? "bg-muted text-foreground hover:bg-muted/80" : "bg-muted text-muted-foreground cursor-not-allowed"}`}>
            <MessageCircle className="w-5 h-5" /> Enquire via WhatsApp
          </button>
        ) : variant ? (
          <button onClick={handleCheckout} disabled={!isFormComplete}
            className={`w-full flex items-center justify-center gap-2 py-4 rounded-2xl text-base font-bold transition-all ${isFormComplete ? "ghana-checkout-gradient text-primary-foreground shadow-elevated hover:shadow-card active:scale-[0.98]" : "bg-muted text-muted-foreground cursor-not-allowed"}`}>
            <MessageCircle className="w-5 h-5" /> Pre-order via WhatsApp
          </button>
        ) : null}

        {variant && <p className="text-[10px] text-center text-muted-foreground">🚚 {selectedShippingOption?.description || "Delivered within 5 business days across Ghana"}</p>}

        <AdSlotRenderer position="inline-form-bottom" />
      </div>
      {adminSettings.reviewsInOrderForm && <CustomerReviews deviceName={device?.name} />}

      {/* Inquiry Popup Dialog */}
      <Dialog open={inquiryOpen} onOpenChange={(open) => { setInquiryOpen(open); if (!open) setContactInquiryMode(false); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-base">{contactInquiryMode ? "Contact Us" : "Inquire About a Lower Price"}</DialogTitle>
            <DialogDescription className="text-xs">
              {contactInquiryMode
                ? "Please provide your details so we can assist you."
                : (device ? `${deviceDisplayName}${variant ? ` ${variant.label}` : ""}` : "General inquiry") +
                  (variant && !isUnavailable ? ` — Listed at ${formatPrice(displayPrice, displayCurrency)}` : "")
              }
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Your Name <span className="text-destructive">*</span></label>
              <input type="text" value={inquiryName} onChange={e => setInquiryName(sanitizeText(e.target.value, 100))} placeholder="Full name" maxLength={100}
                className="w-full bg-muted text-foreground rounded-lg px-3 py-2.5 text-sm border border-border focus:ring-2 focus:ring-ring focus:outline-none" />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Location <span className="text-destructive">*</span></label>
              <input type="text" value={inquiryLocation} onChange={e => setInquiryLocation(sanitizeText(e.target.value, 100))} placeholder="City / Town" maxLength={100}
                className="w-full bg-muted text-foreground rounded-lg px-3 py-2.5 text-sm border border-border focus:ring-2 focus:ring-ring focus:outline-none" />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">WhatsApp Number <span className="text-destructive">*</span></label>
              <input type="tel" value={inquiryPhone} onChange={e => setInquiryPhone(sanitizePhone(e.target.value, 15))} placeholder="e.g. 0598429052" maxLength={15}
                className={`w-full bg-muted text-foreground rounded-lg px-3 py-2.5 text-sm border focus:ring-2 focus:ring-ring focus:outline-none ${inquiryPhone.trim() && !inquiryPhoneValid ? 'border-destructive' : 'border-border'}`} />
              {inquiryPhone.trim() && !inquiryPhoneValid && (
                <p className="text-[10px] text-destructive">Valid Ghana phone required: exactly 10 digits or 12-13 starting with 233</p>
              )}
            </div>
            <button onClick={contactInquiryMode ? handleContactInquirySubmit : handleInquirySubmit}
              disabled={!inquiryName.trim() || !inquiryLocation.trim() || !inquiryPhone.trim() || !inquiryPhoneValid}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold transition-all bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed">
              <MessageCircle className="w-4 h-4" /> {contactInquiryMode ? "Send via WhatsApp" : "Send Inquiry via WhatsApp"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
});

OrderForm.displayName = "OrderForm";
export default OrderForm;
